import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { EnvironmentService } from '@innovation-os-ng/core/utils';
import { CompoundFilter, Filter, IqlQueryOperator, LeafFilter } from '@itonics/filters';

import {
  AggregateDataType,
  AggregationParam,
  CombinableAggregationRequest,
  CountRequest,
  DocumentsQueryAggregation,
  DocumentsQueryResult,
  EntityDetailQueryPostBody,
  EntityDetailQueryPostResult,
  ExecutionDetailAsync,
  InsightsDocument,
  QueryResults,
  SignalsAggregateResult,
  SignalsType
} from '../../../model/types';

import { ApiService } from './api.service';
import { PropertiesService } from './properties.service';
import { QueueService } from './queue.service';

import { findLeafByOnAndExcludeWithValue } from '../../../helpers/filter-helper';
import { extractChild } from '../../../helpers/hierarchical-helper';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skipper, SpaceSelectors } from '@innovation-os-ng/core/state';
import { ToastNotificationsService, ToastType } from '@innovation-os-ng/core/toast-notifications';
import { TranslateService } from '@itonics/ngx-translate-simple';
import { Store } from '@ngrx/store';
import { debounceTime, take } from 'rxjs/operators';
import { DefaultSignalTypes } from '../../../../feed-widget-v2/constants/default-signal-types';
import { CountriesMap } from '../../../../feed-widget-v2/model/countries';
import { CIRCULAR_DEPENDENCY_ERROR_MSG, SignalsProperties, TOAST_CIRCULAR_DEPENDENCY_ID } from '../../../constants/constants';
import { normalize } from '../../../helpers/normalize-helper';
import { ASYNC_METRIC_OPTIONS, MetricOptions } from '../../../model/metric';
import { ASYNC_PROPERTY_OPTIONS } from '../../../model/property';
import { MetricService } from './metric.service';

// Number of aggregations to fetch (backend defaults to 100)
const DEFAULT_AGGREGATION_SIZE = 100;

enum MAX_COMBINED_AGGREGATION_QUERIES {
  SYNC = 10,
  ASYNC = 1
}

export interface RecoveryState {
  controllingProcessId: string;
  errorCode: number;
  requestNumber: number;
  notificationId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  // Queue of pending aggregate requests that can be combined with incoming requests
  // based on matching filter queries
  private combinableAggregateRequestsQueue: Array<CombinableAggregationRequest> = [];
  // Debounceable subject that will submit the pending requests
  private readonly debouncePendingAggregateRequests: Subject<void> = new Subject<void>();

  private recoveryState: RecoveryState = null;

  private currentSpaceUri: string

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly http: HttpClient,
    private readonly queue: QueueService,
    private readonly apiService: ApiService,
    private readonly toastNotificationsService: ToastNotificationsService,
    private readonly propertiesService: PropertiesService,
    private readonly translate: TranslateService,
    private readonly metricService: MetricService,
    private readonly store: Store
  ) {
    // Set up a debounced listener that will fire after multiple requests have been collected (and combined)
    // 10ms is a balanced debounce time that shouldn't noticeably delay the requests, while still giving enough
    // time for all of the requests to be queued
    this.debouncePendingAggregateRequests.pipe(debounceTime(10), takeUntilDestroyed()).subscribe(() => {
      this.sendPendingAggregateRequests();
    });
    this.store
      .select(SpaceSelectors.selectCurrentSpaceUri)
      .pipe(skipper(), takeUntilDestroyed())
      .subscribe({
        next: (currentSpaceUri: string) => {
          this.currentSpaceUri = currentSpaceUri;
        }
      });
  }

  public getDocuments(filter: Filter, paginationCursor: string): Observable<QueryResults<InsightsDocument>> {
    console.assert(!!this.currentSpaceUri, 'currentSpaceUri should not be null')
    const url = this.environmentService.getRestUrl(`/insights-search/insights-search/${this.currentSpaceUri}`);

    const fetchDocuments = this.http.post<any>(url, {
      query: filter.toJson(true),
      paginationCursor
    });

    return this.queue.add(fetchDocuments, 'fetchDocuments');
  }

  /**
   * Add a value to the desired property filter, or create a new filter if it doesn't already exist
   * Eg. to add 'Germany' to the 'countries' filter
   * NOTE: If only the 'opposite' filter (exclude/select) exists, this function will just
   * remove the value from the 'opposite' filter
   *
   * @param filter Parent filter
   * @param property Property we're adding a value to
   * @param value Value to add to the given property
   * @param exclude Exclusion filter or selection filter
   *
   * @returns True if a value was added, false otherwise
   */
  public findLeafAndAddValue(filter: CompoundFilter, property: string, value: string, exclude: boolean): boolean {
    const filterWithProperty = filter.findQuery(findLeafByOnAndExcludeWithValue(property, exclude));
    const oppositeFilterWithProperty = filter.findQuery(findLeafByOnAndExcludeWithValue(property, !exclude));

    // If there's no matching filter (and no opposite filter), create the desired filter
    if (!filterWithProperty && !oppositeFilterWithProperty) {
      const newFilter = new LeafFilter(property).setValues([value]);
      filter.addFilter(newFilter);
      return true;

      // If the filter already exists, add the value to it (if necessary)
    } else if (filterWithProperty instanceof LeafFilter) {
      const leafValues = filterWithProperty.getValues();
      const checkInValues = leafValues.find((val) => val === value);

      // If the new value isn't already in the filter, add it and replace the existing property
      // filter (to trigger any listeners to update)
      if (!checkInValues) {
        leafValues.push(value);
        const updatedFilter = filterWithProperty.clone().setValues(leafValues);
        filter.addFilter(updatedFilter, (oldFilter) => oldFilter === filterWithProperty);
        return true;
      }
    }

    // If the opposite filter (regarding exclusion/selection) exists, make sure the value
    // that's being added is (also) removed from the opposite filter
    // eg. if selecting 'Germany', remove it from the exclusion filter
    if (oppositeFilterWithProperty instanceof LeafFilter) {
      const leafValues = oppositeFilterWithProperty.getValues();
      const newLeafValues = leafValues.filter((val) => val !== value);

      // If filtering out the new value changes the length of values, we need to update the filter
      // by replacing it (to trigger any listeners)
      if (leafValues.length !== newLeafValues.length) {
        const updatedFilter = oppositeFilterWithProperty.clone().setValues(newLeafValues);
        filter.addFilter(updatedFilter, (oldFilter) => oldFilter === oppositeFilterWithProperty);
        return true;
      }
    }

    return false;
  }

  /**
   * Remove the given value from the given leaf filter
   * (if the filter contains the value)
   *
   * @param leafFilter Leaf filter that has values
   * @param value Value to remove from the filter
   *
   * @returns True if a value was removed, false otherwise
   */
  public removeValueFromLeaf(leafFilter: LeafFilter, value): boolean {
    const values = leafFilter?.getValues() || [];
    const valueIndex = values.indexOf(value);

    if (valueIndex !== -1) {
      values.splice(valueIndex, 1);
      return true;
    }
    return false;
  }

  public getPropertyValueSuggestion(property: string, value: string): Observable<Array<string>> {
    console.assert(!!this.currentSpaceUri, 'currentSpaceUri should not be null')
    const fetchSuggestion = this.http.get<any>(
      this.environmentService.getRestUrl(`/insights-search/suggestions/${this.currentSpaceUri}/values`),
      {
        params: { property, value }
      }
    );

    return this.queue.add(fetchSuggestion, property + 'Suggestion');
  }

  /**
   * Determines whether the {@link aggregation} is required to be computed asynchronously or not.
   *
   * @param aggregation the {@link AggregationParam}
   * @returns `true` if it requires asynchronous query
   */
  private isAggregationRequiresAsyncProcessing(aggregation: AggregationParam | string) {
    if (typeof aggregation !== 'string') {
      return ASYNC_PROPERTY_OPTIONS.has(aggregation.property);
    }
    return false;
  }

  /**
   * Fetch aggregate result counts for the given query
   * To save server load, aggregations for the same query will be combined into one request
   * Request will be sent after a short debounce
   *
   * @param type type of aggregations, e.g.country
   * @param query filter (requests with the same filter will be collected and sent together
   * @param metricOptions what metric and metric function to use to calculate values
   * @param id If provided will replace all pending previous calls with the same ID (combined ID)
   * @param aggregationSize Number of aggregates to fetch (defaults to 100)
   *
   * @returns Promise<InsightsAggregateResult>
   */
  public getAggregateData(
    type: string,
    query: CompoundFilter,
    metricOptions: MetricOptions,
    id: string = '',
    aggregationSize: number = DEFAULT_AGGREGATION_SIZE
  ): Promise<SignalsAggregateResult> {
    return new Promise((resolve, reject) => {
      if (!query) {
        reject(null);
        return;
      }

      // Remove any pending requests for the same id
      this.removeExistingDuplicateRequests(id, type);

      // The aggregation portion of the request
      const aggregation = {
        property: type,
        /*
          we keep this as empty string to have all the missing values including the documents
          with an empty string stored as a country, this will be solved here
          https://itonics.atlassian.net/browse/CIG-337
        */
        returnMissingValuesAs: '',
        ...metricOptions,
        aggregationSize
      };

      // Certain metric options should be requested asynchronously because calculating
      // their results will otherwise cause a synchronous request to timeout.
      // Similar to metric options, there are certain aggregations that are very expensive to compute
      // ...and should be handled asynchronously
      const async = ASYNC_METRIC_OPTIONS.indexOf(metricOptions?.metric) !== -1 || this.isAggregationRequiresAsyncProcessing(aggregation);

      // Limit on how many aggregations can be requested
      // When using async (computationally intensive) queries,
      // lower the limit to reduce the load on elasticsearch
      const maxAggregationCount = async ? MAX_COMBINED_AGGREGATION_QUERIES.ASYNC : MAX_COMBINED_AGGREGATION_QUERIES.SYNC;

      // Find the existing queued request with the same filter query AND the same async property (if it exists)
      //
      // Two requests are considered the same if their query are equal AND they require the async property.
      const matchingRequest = this.combinableAggregateRequestsQueue?.find(
        (request) => request.ids.length < maxAggregationCount && request.query.equals(query) && request.async === async
      );

      // A subject that will emit the request's response
      let responseSub: Subject<DocumentsQueryResult>;

      // If there is already a queued request, add this aggregation to that
      if (matchingRequest) {
        matchingRequest.postBody.aggregations.push(aggregation);
        matchingRequest.ids.push(id);
        responseSub = matchingRequest.responseSub;
      } else {
        // Otherwise, build and queue a new request
        responseSub = new Subject<DocumentsQueryResult>();
        this.combinableAggregateRequestsQueue.push({
          type,
          query,
          metricOptions,
          ids: [id],
          postBody: {
            aggregations: [aggregation],
            query: query.clone().toJson(true),
            limit: 0,
            executionDetails: async ? { asynchronous: ExecutionDetailAsync.Aggregations } : null
          },
          responseSub,
          async
        });
      }

      // Subscribe to the new/existing response Subject and handle the output
      responseSub.pipe(take(1)).subscribe(this.resolveAggregateResults(type, metricOptions, resolve), (error) => {
        if (error.error) {
          this.checkCircularDependency(error.error);
        }
        reject(error);
      });

      // Notify the listener that it should send the requests
      // This will be debounced so that we can wait for any more combinable requests
      this.debouncePendingAggregateRequests.next();
    });
  }

  /**
   * Find any existing requests that should be removed (so we're only making one request for each aggregate)
   * If the duplicate request is only for that aggregate, remove it from the queue
   * However, if the duplicate request contains other aggregates, we have to remove only the aggregation-fragment
   * of the request that is related to the given id/type
   *
   * @param id Match any existing request/fragments by this id
   * @param type Type of aggregation requested
   */
  private removeExistingDuplicateRequests(id: string, type: string) {
    // Find a pending request that contains the given ID
    const duplicateQueuedRequest = id && this.combinableAggregateRequestsQueue?.find((request) => request.ids.indexOf(id) !== -1);

    if (duplicateQueuedRequest?.ids.length === 1) {
      // If that request was for only this aggregation, remove it and complete the Subject
      this.combinableAggregateRequestsQueue = this.combinableAggregateRequestsQueue.filter((request) => request !== duplicateQueuedRequest);
      duplicateQueuedRequest.responseSub.complete();
    } else if (duplicateQueuedRequest) {
      // If that request was for other aggregations as well,
      // just remove the fragment of the postBody that applies to this type of aggregation
      duplicateQueuedRequest.ids = duplicateQueuedRequest.ids.filter((queuedId) => queuedId !== id);
      duplicateQueuedRequest.postBody.aggregations = duplicateQueuedRequest.postBody.aggregations.filter(
        // eslint-disable-next-line @typescript-eslint/dot-notation
        (aggregation) => aggregation['property'] !== type
      );
    }
  }

  /**
   * Build & return a function that can extract (and process) the desired aggregation
   * from the API response, before calling the given resolve callback
   *
   * @param type Aggregate we should extract from the results
   * @param metricOptions Metric we're evaluating aggregates for
   * @param resolve Resolve function from parent promise (will resolve to an InsightsAggregateResult)
   *
   * @returns Function that can handle the result of apiService.postDocumentsQuery
   */
  private resolveAggregateResults(type: string, metricOptions: MetricOptions, resolve: (args: any) => void) {
    return (response: DocumentsQueryResult) => {
      let aggregation = [];
      let aggregatesArePaginated: boolean = false;

      if (!response.aggregation?.[type]) {
        resolve({ total: response.total, aggregateData: [] });
        return;
      }

      if (type === SignalsType.COUNTRY) {
        const mergedList = response.aggregation[type].map((d) => d.key);

        //  If we request country data we need to complete the aggregate list
        //  with the countries that we render in the map and we allow to interaction
        const countriesNoData = Object.keys(CountriesMap)
          .filter((key) => mergedList.indexOf(key) === -1)
          .map((key) => ({
            value: 0,
            key
          }));

        aggregation = response.aggregation[type].concat(countriesNoData);
        // Since we're returning all countries, leave the flag `aggregatesArePaginated` as false
      } else if (type === SignalsType.DOCUMENT_TYPE) {
        // If we request document type aggregations, we also want to complete that list
        // with any types that have a value of 0
        const returnedTypes = response.aggregation[type].map((typeData) => typeData.key);

        const defaultSignalTypes: Array<string> = DefaultSignalTypes.map((t) => t.uri);

        const missingTypes = defaultSignalTypes
          .filter((docType) => returnedTypes.indexOf(docType) === -1)
          .map((docType) => ({
            key: docType,
            value: 0
          }));

        aggregation = [...response.aggregation[type], ...missingTypes];
      } else {
        aggregation = response.aggregation[type];

        // If we get the number of aggregates we asked for, assume there are more
        // (it's a bit hacky, but the boolean below is only used to show a '+' in the UI)
        aggregatesArePaginated = aggregation.length === DEFAULT_AGGREGATION_SIZE;
      }

      let isFact = false;
      let isAggregateTypeDifferent = false;

      isFact = SignalsProperties.find((property) => (property.aggregateType || property.type) === type)?.factEntity;
      isAggregateTypeDifferent = SignalsProperties.find((property) => property.aggregateType && !property.oldType) !== undefined;

      resolve({
        total: response.total,
        aggregateData: this.buildAggregateData(
          aggregation,
          // normalize ? if we are showing relevance metrics we normalize
          this.metricService.isMetricLikeRelevance(metricOptions.metric),
          isFact,
          isAggregateTypeDifferent,
          this.propertiesService.getInsightsProperties().find((prop) => (prop.aggregateType || prop.type) === type).dictionary
        ),
        aggregatesArePaginated
      });
    };
  }

  /**
   * Send all pending requests and empty the queue
   */
  private sendPendingAggregateRequests(): void {
    this.combinableAggregateRequestsQueue?.forEach((request) => this.sendPendingAggregateRequest(request));

    // Clean up queue
    this.combinableAggregateRequestsQueue = [];
  }

  /**
   * Send the pending request and route its response/error to the request's `responseSub`
   *
   * @param request Aggregation request, containing a responseSub that we can output response/error to
   */
  private sendPendingAggregateRequest(request: CombinableAggregationRequest) {
    const requestId = request.async ? `async-${request.ids.join(' - ')}` : request.ids.join(' - ');
    this.apiService.postDocumentsQuery(request.postBody, requestId).then(
      (response) => request.responseSub.next(response),
      (error) => request.responseSub.error(error)
    );
  }

  private checkCircularDependency(error: { message: string }) {
    if (error.message && error.message.indexOf(CIRCULAR_DEPENDENCY_ERROR_MSG) !== -1) {
      this.toastNotificationsService.showToast({
        toastId: TOAST_CIRCULAR_DEPENDENCY_ID,
        // We take the translation from the insights app as it is a service
        content: this.translate.instant('signals-ng-shared-services.common.error.circularDependency'),
        duration: 10000,
        type: ToastType.ERROR
      });
    }
  }

  /**
   * Data builders
   */

  private buildAggregateData(
    data: Array<DocumentsQueryAggregation>,
    normalizeData: boolean,
    isFactEntity?: boolean,
    aggregateTypeDifferent?: boolean,
    dictionary?: any
  ) {
    let normalizeFnc;

    if (normalizeData) {
      const values = data.map((d) => d.value);
      const max = Math.max(...values);

      normalizeFnc = (value) => normalize(0, max, value);
    }

    return data.map((d: any) => {
      const label = !!d.key
        ? dictionary && dictionary[d.key]
          ? dictionary[d.key].label
            ? dictionary[d.key].label
            : dictionary[d.key]
          : d.key
        : // We take the translation from the insights app as it is a service
          this.translate.instant('signals-ng-shared-services.propertyLabels.notAssigned');

      let aggId;
      if (aggregateTypeDifferent) {
        aggId = d.label;
      }

      return {
        label: isFactEntity ? d.label : extractChild(label),
        id: aggId ? aggId : !!d.key ? d.key : AggregateDataType.NOT_ASSIGNED,
        value: normalizeData ? normalizeFnc(d.value) : d.value
      };
    });
  }

  public getSearchFieldDocCount(spaceUri: string, postBody: { countRequests: Array<CountRequest> }, id?: string) {
    return this.apiService.postSearchFieldDocCount(spaceUri, postBody, id);
  }

  /**
   * Handle a user clicking on a signal's property (eg. on a Tag or Fact)
   * Generally, this will add that property to the appropriate part of the selection filter
   * If there's currently an exclusion filter for that property, we just make sure that the clicked
   * value is not excluded
   *
   * @param selectionFilter The global selection filter
   * @param property The type of property that got clicked
   * @param value The value of the property that got clicked
   * @param usesEntityUri Set true for Fact-based properties that set values on entityUris
   *
   * @returns True if the filter was updated, false otherwise
   */
  public documentPropertyClicked(selectionFilter: CompoundFilter, property: string, value: string, usesEntityUri?: boolean): boolean {
    const leafProperty = usesEntityUri ? SignalsType.ENTITY_URIS : property;

    // Some extra logic here to handle the case where the property filter is represented
    // by a Compound instead of a Leaf (this happens when we're selecting/excluding 'No Value')
    const propertyFilter = selectionFilter.findQuery(
      (query) => (query instanceof LeafFilter ? query.getOn() : query.getIdentifier()) === property
    );

    // Track whether we need to trigger an update on the selection filter to notify listeners
    let shouldUpdateSelectionFilter: boolean;

    // Track whether any filter was updated (this won't always be the same as shouldUpdateSelectionFilter)
    let filterWasUpdated: boolean = false;

    // Set true when the filter is removed (because we removed the last excluded value)
    let removedPropertyFilter: boolean = false;

    // The filter doesn't exist yet, so add it
    if (!propertyFilter) {
      // Simple case: just add a leaf filter with the given value
      if (property === leafProperty) {
        this.findLeafAndAddValue(selectionFilter, leafProperty, value, false);

        // For some Fact filters, we make an identifying CompoundFilter that then wraps the new LeafFilter
      } else {
        const wrappingFilter = new CompoundFilter(IqlQueryOperator.OR).setIdentifier(property);
        this.findLeafAndAddValue(wrappingFilter, leafProperty, value, false);
        selectionFilter.addFilter(wrappingFilter);
      }

      filterWasUpdated = true;

      // Simple case: we just have a Leaf, so add value where appropriate
    } else if (propertyFilter instanceof LeafFilter) {
      filterWasUpdated = this.findLeafAndAddValue(selectionFilter, leafProperty, value, false);

      // If we have an inclusion CompoundFilter, add the value to the relevant child of that filter
    } else if (propertyFilter instanceof CompoundFilter && !propertyFilter.getExclude()) {
      shouldUpdateSelectionFilter = this.findLeafAndAddValue(propertyFilter, leafProperty, value, false);
      filterWasUpdated = shouldUpdateSelectionFilter;

      // Finally, if we have an excluded CompoundFilter, remove the value from the appropriate child of that filter
    } else if (propertyFilter instanceof CompoundFilter && propertyFilter.getExclude()) {
      const propertyLeafFilter = propertyFilter.findQuery(findLeafByOnAndExcludeWithValue(leafProperty, false)) as LeafFilter;
      shouldUpdateSelectionFilter = this.removeValueFromLeaf(propertyLeafFilter, value);
      filterWasUpdated = shouldUpdateSelectionFilter;

      // If we've removed the last item from the LeafFilter, remove the whole leaf
      if (!propertyLeafFilter.getValues()?.length) {
        propertyFilter.removeFilter(propertyLeafFilter);
      }

      // If there's nothing else in the parent CompoundFilter, remove that too
      if (!propertyFilter.getQueries()?.length) {
        selectionFilter.removeFilter(propertyFilter);

        removedPropertyFilter = true;
      }
    }

    // If necessary, trigger an update here because some components are only listening to updates on the selection filter
    if (shouldUpdateSelectionFilter && !removedPropertyFilter) {
      selectionFilter.addFilter(propertyFilter, (oldFilter) => oldFilter === propertyFilter);
    }

    return filterWasUpdated;
  }

  /**
   * Get data about the current error/recovery state (used when handling server errors)
   *
   * @returns The stored data about the current error/recovery state
   */
  public getRecoveryState(): RecoveryState | null {
    return this.recoveryState;
  }

  /**
   * Store (shared) data about the current error/recovery state (when handling server errors)
   *
   * @param recoveryState State information to store
   */
  public setRecoveryState(recoveryState: RecoveryState) {
    this.recoveryState = recoveryState;
  }

  /**
   * Retrieve entity details for provided entity array of type EntityDetailQueryPostBody
   *
   * @param entities Entity uris to retrieve details for
   *
   * @returns The details for the provided entities
   */
  public getEntityDetails(entities: EntityDetailQueryPostBody): Promise<Array<EntityDetailQueryPostResult>> {
    return this.apiService.postEntityDetailsQuery(entities);
  }
}
